/**
 * IntakeProbe.jsx — iPhone file-input bridge forensic probe.
 * Expanded May 22, 2026 to answer the 10 operator diagnostic
 * questions in one iPhone test session.
 *
 * NOT part of any upload subsystem. NOT part of the Video Analysis
 * Engine. NOT integrated with state, baskets, modals, or any global
 * recovery system. A leaf diagnostic page whose only job is to ask:
 *
 *   What does iOS Safari hand back to onChange when the user picks
 *   videos from the Photos app, under N different input
 *   configurations?
 *
 * Mounted at /intake-probe (no auth, see authAxios.js public list).
 *
 * Hot-path rules (onChange):
 *   - NO state updates before the synchronous dump.
 *   - NO awaits.
 *   - NO input.value clearing.
 *   - NO modal changes.
 *   - Append to a ref-held array; render is the LAST thing.
 *
 * 8 variants — covers operator Q1–Q10:
 *
 *   A) hidden + button-triggered, accept="video/*", multiple
 *   B) hidden + button-triggered, accept="image/*,video/*", multiple
 *   C) hidden + button-triggered, no accept attribute, multiple
 *   D) VISIBLE native input, accept="video/*", multiple
 *      (the failing one in the operator's iPhone test)
 *   E) VISIBLE native input, NO accept, NO multiple — plain
 *   F) VISIBLE native input, accept="video/*", NO multiple
 *   G) VISIBLE native input, accept="video/*", capture="environment"
 *      → direct camera capture (operator Q10 fallback)
 *   H) VISIBLE native input, accept="image/*" — image-only sanity
 *      (if H works but D fails, Photos→input bridge is broken for
 *      video specifically; if H also fails, the bridge is broken
 *      for ALL Photos handoffs)
 *
 * Per-input instrumentation:
 *   - change, input, cancel, click, focus, blur listeners
 *   - Polling watcher: after click, poll input.files every 200ms for
 *     8 s. If files.length > 0 without onChange firing, log as an
 *     iOS bug signature ("files attached, no change event").
 *   - MutationObserver on the input's parent — detects remount/
 *     recreate between blur and focus (operator Q9).
 *   - Identity-check at every lifecycle event — compares the current
 *     ref.current to the last seen reference (detects React replacing
 *     the input element).
 *
 * Page-level:
 *   - Environment dump: UA, isStandalone, isInIframe, platform,
 *     maxTouchPoints, screen, protocol, origin (production vs preview
 *     — operator Q7), document.cookie reachable, document.referrer.
 *   - Desktop-mode detection (operator Q8).
 *   - Lifecycle log with pageshow.persisted, beforeunload, unload.
 *   - Decision-tree summary at the top (per-variant green/red).
 */
import { useEffect, useRef, useState } from "react";

const STORAGE_KEY = "intake-probe:dumps";
const POLL_MS = 200;
const POLL_DURATION_MS = 8_000;

// ── synchronous helpers ───────────────────────────────────────────

function safeNow() { try { return performance.now(); } catch (_) { return Date.now(); } }
function safeISO() { try { return new Date().toISOString(); } catch (_) { return ""; } }
function computedStyleSnapshot(el) {
  try {
    if (!el || !window.getComputedStyle) return null;
    const cs = window.getComputedStyle(el);
    return { display: cs.display, visibility: cs.visibility, opacity: cs.opacity,
             position: cs.position, width: cs.width, height: cs.height,
             pointerEvents: cs.pointerEvents };
  } catch (_) { return null; }
}
function describeFiles(fileList) {
  if (!fileList) return null;
  const out = [];
  for (let i = 0; i < fileList.length; i++) {
    const f = fileList[i];
    out.push({
      idx: i, name: f?.name ?? null, size: f?.size ?? null,
      type: f?.type ?? null, lastModified: f?.lastModified ?? null,
      isFileInstance: typeof File !== "undefined" ? f instanceof File : null,
      isBlobInstance: typeof Blob !== "undefined" ? f instanceof Blob : null,
      constructorName: f?.constructor?.name ?? null,
    });
  }
  return out;
}
function persistDumps(dumps) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(dumps).slice(0, 1_000_000)); }
  catch (_) { /* quota */ }
}
function loadDumps() {
  try { const r = localStorage.getItem(STORAGE_KEY); return r ? JSON.parse(r) : []; }
  catch (_) { return []; }
}

// ── environment snapshot (one-shot at mount) ──────────────────────

function snapshotEnvironment() {
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const platform = typeof navigator !== "undefined" ? navigator.platform : "";
  const maxTouchPoints = typeof navigator !== "undefined" ? navigator.maxTouchPoints : null;
  // iPad + iPhone in "Request Desktop Site" mode → UA reports Macintosh
  // with maxTouchPoints > 0. Pure iPhone mobile mode → UA includes iPhone.
  const looksLikeiPhone = /iPhone/i.test(ua);
  const looksLikeiPadOrDesktopMode =
    /Macintosh/.test(ua) && (maxTouchPoints || 0) > 1;
  const isDesktopModeOnPhone = !looksLikeiPhone && looksLikeiPadOrDesktopMode;
  let isInIframe = false;
  try { isInIframe = window.self !== window.top; } catch (_) { isInIframe = true; }
  let isStandalone = false;
  try {
    isStandalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
  } catch (_) { /* noop */ }
  return {
    wall: safeISO(),
    userAgent: ua,
    platform,
    maxTouchPoints,
    looksLikeiPhone,
    looksLikeiPadOrDesktopMode,
    isDesktopModeOnPhone,
    isInIframe,
    isStandalone,
    protocol: typeof location !== "undefined" ? location.protocol : null,
    origin: typeof location !== "undefined" ? location.origin : null,
    href: typeof location !== "undefined" ? location.href : null,
    referrer: typeof document !== "undefined" ? document.referrer : null,
    screen: typeof screen !== "undefined"
      ? { w: screen.width, h: screen.height, dpr: window.devicePixelRatio }
      : null,
    cookieEnabled: typeof navigator !== "undefined" ? navigator.cookieEnabled : null,
    languages: typeof navigator !== "undefined" ? navigator.languages : null,
  };
}

// ── component ─────────────────────────────────────────────────────

export default function IntakeProbe() {
  const dumpsRef = useRef(loadDumps());
  const [, force] = useState(0);
  const inputRefs = useRef({});           // { A: el, B: el, … }
  const lastClickRef = useRef({});
  const pollersRef = useRef({});          // { A: intervalId, … }
  const observersRef = useRef({});        // { A: MutationObserver, … }
  const inputIdentityRef = useRef({});    // tracks if React replaces the element
  const envRef = useRef(snapshotEnvironment());

  function log(kind, payload) {
    dumpsRef.current.push({ kind, wall: safeISO(), perfMs: safeNow(), ...payload });
    persistDumps(dumpsRef.current);
    force((n) => n + 1);
  }

  // ── lifecycle listeners (operator Q9 — detect bfcache, reloads) ─
  useEffect(() => {
    log("lifecycle", { type: "mount", env: envRef.current });

    const onShow = (e) => log("lifecycle", {
      type: "pageshow", persisted: !!e?.persisted,
      visibility: document.visibilityState,
    });
    const onHide = (e) => log("lifecycle", {
      type: "pagehide", persisted: !!e?.persisted,
    });
    const onVis = () => log("lifecycle", {
      type: "visibilitychange", visibility: document.visibilityState,
    });
    const onFocus = () => log("lifecycle", { type: "window_focus" });
    const onBlur = () => log("lifecycle", { type: "window_blur" });
    const onBefore = () => log("lifecycle", { type: "beforeunload" });
    const onUnload = () => log("lifecycle", { type: "unload" });
    const onLoad = () => log("lifecycle", { type: "window_load" });

    window.addEventListener("pageshow", onShow);
    window.addEventListener("pagehide", onHide);
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onFocus);
    window.addEventListener("blur", onBlur);
    window.addEventListener("beforeunload", onBefore);
    window.addEventListener("unload", onUnload);
    window.addEventListener("load", onLoad);

    return () => {
      window.removeEventListener("pageshow", onShow);
      window.removeEventListener("pagehide", onHide);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("beforeunload", onBefore);
      window.removeEventListener("unload", onUnload);
      window.removeEventListener("load", onLoad);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── attach a forensic listener bundle + observer to each input ──
  function attachInput(variant, el) {
    if (!el) return;
    const prev = inputRefs.current[variant];
    if (prev === el) return;             // same element, already wired
    inputRefs.current[variant] = el;
    const prevId = inputIdentityRef.current[variant];
    if (prevId && prevId !== el) {
      log("input_remounted", { variant, note: "React replaced input element" });
    }
    inputIdentityRef.current[variant] = el;

    // Listeners — every event that might tell us something.
    el.addEventListener("change", onChangeFor(variant));
    el.addEventListener("input", (e) => snapshot(variant, "input", e));
    el.addEventListener("cancel", (e) => snapshot(variant, "cancel", e));
    el.addEventListener("focus", () => snapshot(variant, "focus_input", null));
    el.addEventListener("blur", () => snapshot(variant, "blur_input", null));
    el.addEventListener("click", () => {
      // Native user click on a visible input (variants D–H). Mirrors
      // the explicit recordClick used by the hidden+button variants.
      recordClick(variant);
    });

    // MutationObserver on parent — operator Q9 (remount/recreate).
    try {
      const parent = el.parentElement;
      if (parent && !observersRef.current[variant]) {
        const obs = new MutationObserver((muts) => {
          muts.forEach((m) => log("mutation", {
            variant,
            type: m.type,
            removedNodes: m.removedNodes.length,
            addedNodes: m.addedNodes.length,
          }));
        });
        obs.observe(parent, { childList: true, attributes: true, subtree: false });
        observersRef.current[variant] = obs;
      }
    } catch (_) { /* noop */ }
  }

  function snapshot(variant, evtype, evt) {
    const el = inputRefs.current[variant];
    log("event", {
      variant, evtype,
      eventType: evt?.type ?? null,
      filesLength: el?.files?.length ?? null,
      inputValue: el?.value ?? null,
    });
  }

  function recordClick(variant) {
    const el = inputRefs.current[variant];
    lastClickRef.current[variant] = {
      perfMs: safeNow(), wall: safeISO(),
      style: computedStyleSnapshot(el),
    };
    log("picker_click", { variant });
    startPolling(variant);
  }

  // Polling watcher — operator Q9 catches the "files attached, no
  // change event" iOS pattern. Polls input.files for POLL_DURATION_MS
  // after each click; if length ever > 0, log it. Cancels itself on
  // any change event or after the timeout.
  function startPolling(variant) {
    if (pollersRef.current[variant]) {
      clearInterval(pollersRef.current[variant]);
    }
    const startedAt = safeNow();
    let lastLen = inputRefs.current[variant]?.files?.length ?? -1;
    const id = setInterval(() => {
      const el = inputRefs.current[variant];
      const len = el?.files?.length ?? -1;
      if (len !== lastLen) {
        log("poll_files_changed", {
          variant, prevLen: lastLen, newLen: len,
          inputValue: el?.value ?? null,
          msSinceClick: safeNow() - (lastClickRef.current[variant]?.perfMs ?? startedAt),
          note: len > 0
            ? "files attached without a change event having fired — iOS bug signature"
            : "files length transitioned",
        });
        lastLen = len;
      }
      if (safeNow() - startedAt > POLL_DURATION_MS) {
        clearInterval(id);
        delete pollersRef.current[variant];
        log("poll_ended", {
          variant, finalLen: len,
          durationMs: safeNow() - startedAt,
        });
      }
    }, POLL_MS);
    pollersRef.current[variant] = id;
  }

  function onChangeFor(variant) {
    return (e) => {
      // Stop polling — onChange fired, we won.
      if (pollersRef.current[variant]) {
        clearInterval(pollersRef.current[variant]);
        delete pollersRef.current[variant];
      }
      const t0 = safeNow();
      const target = e?.target ?? null;
      const currentTarget = e?.currentTarget ?? null;
      const refEl = inputRefs.current[variant];
      const targetFiles = target?.files ?? null;
      const currentTargetFiles = currentTarget?.files ?? null;
      const refFiles = refEl?.files ?? null;

      const dump = {
        kind: "onchange", variant,
        wall: safeISO(), perfMs: t0,
        clickedAtPerfMs: lastClickRef.current[variant]?.perfMs ?? null,
        clickedAtWall: lastClickRef.current[variant]?.wall ?? null,
        msSincePickerOpened:
          lastClickRef.current[variant]?.perfMs != null
            ? t0 - lastClickRef.current[variant].perfMs
            : null,
        eventType: e?.type ?? null,
        eventBubbles: e?.bubbles ?? null,
        eventIsTrusted: e?.isTrusted ?? null,
        inputValue: target?.value ?? null,
        inputValueLength: target?.value?.length ?? null,
        targetExists: target !== null,
        currentTargetExists: currentTarget !== null,
        targetFilesExists: !!targetFiles,
        targetFilesLength: targetFiles?.length ?? null,
        currentTargetFilesExists: !!currentTargetFiles,
        currentTargetFilesLength: currentTargetFiles?.length ?? null,
        refFilesExists: !!refFiles,
        refFilesLength: refFiles?.length ?? null,
        targetIsRef: target === refEl,
        currentTargetIsRef: currentTarget === refEl,
        targetSampleFromTarget: describeFiles(targetFiles),
        targetSampleFromRef: describeFiles(refFiles),
        inputAttrs: {
          accept: refEl?.getAttribute?.("accept") ?? null,
          capture: refEl?.getAttribute?.("capture") ?? null,
          multiple: refEl?.hasAttribute?.("multiple") ?? null,
          type: refEl?.getAttribute?.("type") ?? null,
        },
        inputComputedStyle: computedStyleSnapshot(refEl),
        userAgent: envRef.current.userAgent,
      };
      dumpsRef.current.push(dump);
      persistDumps(dumpsRef.current);
      force((n) => n + 1);
    };
  }

  function clearAll() {
    dumpsRef.current = [];
    persistDumps([]);
    force((n) => n + 1);
  }
  function copyAll() {
    try {
      const text = JSON.stringify(
        { env: envRef.current, dumps: dumpsRef.current },
        null, 2,
      );
      if (navigator?.clipboard?.writeText) navigator.clipboard.writeText(text);
      else window.prompt("Copy this log:", text);
    } catch (_) { /* noop */ }
  }

  // ── derived render data ──────────────────────────────────────────
  const dumps = dumpsRef.current;
  const onChangeByVariant = {};
  const pollByVariant = {};
  for (const d of dumps) {
    if (d.kind === "onchange") {
      const list = onChangeByVariant[d.variant] || [];
      list.push(d);
      onChangeByVariant[d.variant] = list;
    } else if (d.kind === "poll_files_changed") {
      const list = pollByVariant[d.variant] || [];
      list.push(d);
      pollByVariant[d.variant] = list;
    }
  }
  const variantList = ["A", "B", "C", "D", "E", "F", "G", "H"];
  const recentOnChange = [...dumps].reverse()
    .filter((d) => d.kind === "onchange")
    .slice(0, 8);
  const recentLifecycle = [...dumps].reverse()
    .filter((d) => d.kind === "lifecycle" || d.kind === "input_remounted" || d.kind === "mutation")
    .slice(0, 20);
  const recentEvents = [...dumps].reverse()
    .filter((d) => d.kind === "event" || d.kind === "picker_click"
                || d.kind === "poll_files_changed" || d.kind === "poll_ended")
    .slice(0, 30);

  // ── render ───────────────────────────────────────────────────────
  return (
    <div data-testid="intake-probe" style={pageWrap}>
      <h1 style={h1}>iPhone Photos picker — boundary probe v2</h1>
      <p style={pHelp}>
        Eight variants. Tap each and pick videos. Try Photos, then
        try <b>Browse → Files</b> (the picker has a button at the
        bottom of the iOS picker). Try Cellular vs Wi-Fi. Try
        Request Desktop Site. Pick small local clips, then large
        iCloud-only clips. The page logs every signal — no upload,
        no platform side-effects.
      </p>

      {/* ── Summary card ─────────────────────────────────────────── */}
      <div style={{ ...sectionWrap, borderColor: "#2a2a2a" }} data-testid="probe-summary">
        <p style={sectionTitle}>Summary — change events seen per variant</p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 6 }}>
          {variantList.map((v) => {
            const onc = onChangeByVariant[v] || [];
            const lastFilesLen = onc.length
              ? (onc[onc.length - 1].targetFilesLength ?? 0) : null;
            const pollEvents = pollByVariant[v] || [];
            const pollSawFiles = pollEvents.some((p) => p.newLen > 0);
            const status = onc.length === 0 && !pollSawFiles
              ? { color: "#888", label: "—" }
              : onc.length > 0 && (lastFilesLen ?? 0) > 0
                ? { color: "#86efac", label: `${lastFilesLen} ✓` }
                : pollSawFiles
                  ? { color: "#fbbf24", label: "polled" }
                  : { color: "#f87171", label: "no files" };
            return (
              <div key={v}
                data-testid={`probe-summary-${v}`}
                data-status={status.label}
                style={{ background: "#0f0f0f", border: `1px solid ${status.color}33`,
                         borderRadius: 6, padding: "6px 8px", textAlign: "center" }}>
                <div style={{ fontSize: 11, color: "#888" }}>{v}</div>
                <div style={{ fontSize: 13, fontWeight: 800, color: status.color }}>
                  {status.label}
                </div>
                <div style={{ fontSize: 10, color: "#666" }}>
                  events: {onc.length}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ───── Hidden + button-triggered ───── */}
      <HiddenSection
        variant="A" title='A — accept="video/*", multiple, hidden + button'
        accept="video/*" multiple onRef={(el) => attachInput("A", el)}
        recordClick={recordClick} testid="probe-a"
      />
      <HiddenSection
        variant="B" title='B — accept="image/*,video/*", multiple, hidden + button'
        accept="image/*,video/*" multiple onRef={(el) => attachInput("B", el)}
        recordClick={recordClick} testid="probe-b"
      />
      <HiddenSection
        variant="C" title="C — no accept, multiple, hidden + button"
        accept={null} multiple onRef={(el) => attachInput("C", el)}
        recordClick={recordClick} testid="probe-c"
      />

      {/* ───── Visible native inputs ───── */}
      <VisibleSection
        variant="D" title='D — VISIBLE, accept="video/*", multiple'
        accept="video/*" multiple onRef={(el) => attachInput("D", el)}
        testid="probe-d"
      />
      <VisibleSection
        variant="E" title="E — VISIBLE, NO accept, NO multiple (plain)"
        accept={null} multiple={false}
        onRef={(el) => attachInput("E", el)} testid="probe-e"
      />
      <VisibleSection
        variant="F" title='F — VISIBLE, accept="video/*", NO multiple'
        accept="video/*" multiple={false}
        onRef={(el) => attachInput("F", el)} testid="probe-f"
      />
      <VisibleSection
        variant="G" title='G — VISIBLE, accept="video/*", capture="environment" (direct camera)'
        accept="video/*" capture="environment" multiple={false}
        onRef={(el) => attachInput("G", el)} testid="probe-g"
        hint="Opens the rear camera directly. If THIS works on iPhone, it's a viable fallback."
      />
      <VisibleSection
        variant="H" title='H — VISIBLE, accept="image/*" (image-only sanity)'
        accept="image/*" multiple={false}
        onRef={(el) => attachInput("H", el)} testid="probe-h"
        hint="If H works but D fails, the Photos→input bridge is broken for VIDEO specifically."
      />

      {/* ───── Recent onChange events ───── */}
      <h2 style={h2green}>Recent onChange events (newest first)</h2>
      {recentOnChange.length === 0 ? (
        <p style={pNote} data-testid="probe-no-events">
          No onChange events yet. Tap one of the variants above.
        </p>
      ) : (
        <ul style={ul} data-testid="probe-events">
          {recentOnChange.map((d, i) => (
            <EventCard key={i} d={d} />
          ))}
        </ul>
      )}

      {/* ───── Recent per-input events (click, input, cancel, polling) ──── */}
      <h2 style={h2amber}>Per-input event stream (newest first)</h2>
      <ul style={ul} data-testid="probe-stream">
        {recentEvents.map((d, i) => (
          <li key={i} style={streamRow}>
            <span style={{ color: "#fbbf24", fontWeight: 700, marginRight: 8 }}>
              {d.variant || ""}
            </span>
            <span style={{ color: "#888", marginRight: 8 }}>{d.kind}</span>
            <span style={{ color: "#e5e5e5" }}>
              {d.evtype || d.note || `len=${d.newLen ?? d.finalLen ?? ""}`}
            </span>
            <span style={{ color: "#666", float: "right", fontSize: 10 }}>{d.wall}</span>
          </li>
        ))}
      </ul>

      {/* ───── Lifecycle events ───── */}
      <h2 style={h2blue}>Lifecycle + mutation events</h2>
      <ul style={ul} data-testid="probe-lifecycle">
        {recentLifecycle.map((d, i) => (
          <li key={i} style={{ fontSize: 11, color: "#bbb", padding: "2px 0" }}>
            <span style={{ color: "#93c5fd", fontWeight: 700 }}>{d.type || d.kind}</span>
            {d.variant ? ` [${d.variant}]` : ""}
            {d.persisted != null ? ` (persisted=${d.persisted})` : ""}
            {d.visibility ? ` (vis=${d.visibility})` : ""}
            {d.removedNodes != null
              ? ` (removed=${d.removedNodes} added=${d.addedNodes})` : ""}
            {d.note ? ` — ${d.note}` : ""}
            <span style={{ color: "#666", float: "right" }}>{d.wall}</span>
          </li>
        ))}
      </ul>

      {/* ───── Environment ───── */}
      <h2 style={h2blue}>Environment</h2>
      <pre style={pre} data-testid="probe-env">
        {JSON.stringify(envRef.current, null, 2)}
      </pre>

      {/* ───── Controls ───── */}
      <div style={{ marginTop: 20, display: "flex", gap: 8 }}>
        <button type="button" onClick={copyAll} data-testid="probe-copy"
          style={btnPrimary}>Copy full log</button>
        <button type="button" onClick={clearAll} data-testid="probe-clear"
          style={btnSecondary}>Clear log</button>
      </div>

      <p style={{ fontSize: 10, color: "#666", marginTop: 18 }}>
        Log persists in localStorage[<code>{STORAGE_KEY}</code>]
      </p>
    </div>
  );
}

// ── presentational sub-components ─────────────────────────────────

function HiddenSection({ variant, title, accept, multiple, onRef, recordClick, testid }) {
  const localRef = useRef(null);
  useEffect(() => { if (localRef.current) onRef(localRef.current); }, [onRef]);
  return (
    <div style={sectionWrap} data-testid={testid}>
      <p style={sectionTitle}>{title}</p>
      <button type="button" data-testid={`${testid}-btn`}
        onClick={() => { recordClick(variant); localRef.current?.click(); }}
        style={btnPick}>Pick videos</button>
      <input
        ref={localRef}
        type="file"
        {...(accept != null ? { accept } : {})}
        {...(multiple ? { multiple: true } : {})}
        style={hiddenInputStyle}
        data-testid={`${testid}-input`}
      />
    </div>
  );
}

function VisibleSection({ variant, title, accept, multiple, capture, hint, onRef, testid }) {
  const localRef = useRef(null);
  useEffect(() => { if (localRef.current) onRef(localRef.current); }, [onRef]);
  return (
    <div style={sectionWrap} data-testid={testid}>
      <p style={sectionTitle}>{title}</p>
      {hint && <p style={pHelp2}>{hint}</p>}
      <input
        ref={localRef}
        type="file"
        {...(accept != null ? { accept } : {})}
        {...(multiple ? { multiple: true } : {})}
        {...(capture ? { capture } : {})}
        style={visibleInputStyle}
        data-testid={`${testid}-input`}
      />
    </div>
  );
}

function EventCard({ d }) {
  return (
    <li style={eventCard}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
        <span style={{ fontWeight: 800, color: "#fbbf24" }}>Variant {d.variant}</span>
        <span style={{ fontSize: 10, color: "#666" }}>{d.wall}</span>
      </div>
      <div style={{ fontSize: 11, lineHeight: 1.45 }}>
        <KV k="event.type" v={d.eventType} />
        <KV k="event.target.files.length" v={d.targetFilesLength} />
        <KV k="event.currentTarget.files.length" v={d.currentTargetFilesLength} />
        <KV k="ref.files.length" v={d.refFilesLength} />
        <KV k="input.value" v={JSON.stringify(d.inputValue)} />
        <KV k="ms since picker click" v={d.msSincePickerOpened?.toFixed?.(1) ?? null} />
        <KV k="accept" v={d.inputAttrs?.accept} />
        <KV k="capture" v={d.inputAttrs?.capture} />
        <KV k="multiple" v={String(d.inputAttrs?.multiple)} />
      </div>
      {d.targetSampleFromTarget?.length ? (
        <pre style={pre}>{JSON.stringify(d.targetSampleFromTarget, null, 2)}</pre>
      ) : (
        <p style={{ color: "#f87171", fontSize: 11, margin: "6px 0 0 0" }}>
          ⚠️ no files in event.target.files
          {d.targetSampleFromRef?.length
            ? ` — BUT ref.files has ${d.targetSampleFromRef.length}`
            : ` — ref.files also empty`}
        </p>
      )}
    </li>
  );
}

function KV({ k, v }) {
  return (
    <div style={{ display: "flex", gap: 6 }}>
      <span style={{ color: "#888", minWidth: 200 }}>{k}</span>
      <span style={{ color: "#e5e5e5" }}>{v == null ? "null" : String(v)}</span>
    </div>
  );
}

// ── styles ────────────────────────────────────────────────────────

const pageWrap = {
  minHeight: "100vh", background: "#0a0a0a", color: "#e5e5e5",
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  padding: "16px", WebkitFontSmoothing: "antialiased",
};
const h1 = { fontSize: 18, fontWeight: 800, color: "#fbbf24", margin: "0 0 4px 0" };
const h2green = { fontSize: 13, fontWeight: 800, color: "#86efac", margin: "16px 0 6px 0" };
const h2amber = { fontSize: 13, fontWeight: 800, color: "#fbbf24", margin: "16px 0 6px 0" };
const h2blue = { fontSize: 13, fontWeight: 800, color: "#93c5fd", margin: "16px 0 6px 0" };
const pHelp = { fontSize: 11, color: "#888", margin: "0 0 16px 0", lineHeight: 1.5 };
const pHelp2 = { fontSize: 10.5, color: "#888", margin: "0 0 6px 0", lineHeight: 1.45 };
const pNote = { fontSize: 12, color: "#666" };
const ul = { listStyle: "none", padding: 0, margin: 0 };
const hiddenInputStyle = {
  position: "absolute", width: 1, height: 1, opacity: 0,
  pointerEvents: "none", overflow: "hidden",
};
const visibleInputStyle = {
  color: "#e5e5e5", background: "#1a1a1a", border: "1px solid #333",
  borderRadius: 6, padding: "10px 12px", fontSize: 14, width: "100%",
};
const sectionWrap = {
  border: "1px solid #1f1f1f", borderRadius: 8, padding: 12,
  marginBottom: 10, background: "#111",
};
const sectionTitle = {
  fontSize: 12, fontWeight: 700, color: "#e5e5e5", margin: "0 0 8px 0",
};
const btnPick = {
  background: "#fbbf24", color: "#0a0a0a", border: "none",
  padding: "10px 16px", fontWeight: 800, borderRadius: 6, fontSize: 13, width: "100%",
};
const btnPrimary = {
  background: "#3b82f6", color: "white", border: "none",
  padding: "10px 14px", fontWeight: 700, borderRadius: 6, fontSize: 13, flex: 1,
};
const btnSecondary = {
  background: "#1a1a1a", color: "#e5e5e5", border: "1px solid #333",
  padding: "10px 14px", fontWeight: 700, borderRadius: 6, fontSize: 13, flex: 1,
};
const eventCard = {
  background: "#0f0f0f", border: "1px solid #1f1f1f", borderRadius: 6,
  padding: 10, marginBottom: 8,
};
const streamRow = {
  fontSize: 11, color: "#e5e5e5", padding: "3px 0",
  borderBottom: "1px solid #1a1a1a",
};
const pre = {
  background: "#050505", border: "1px solid #1a1a1a", borderRadius: 4,
  padding: 6, fontSize: 10, color: "#86efac", overflowX: "auto",
  margin: "6px 0 0 0", whiteSpace: "pre-wrap",
};
